import { useEffect, useMemo, useRef } from 'react';

import { di } from 'react-magnetic-di';

import { useAIMateExperienceTracker } from '../../controllers/experience-tracker';
import { type ExperienceNameType } from '../../controllers/experience-tracker/constants';

type ExperienceProps = {
	name: ExperienceNameType;
};

type ExperienceSuccessProps = ExperienceProps & {
	/**
	 * Component lifecycle event that should trigger experience success
	 * @default "mount"
	 */
	trigger?: 'mount' | 'unmount';
};

export const ExperienceSuccess = ({ name, trigger = 'mount' }: ExperienceSuccessProps) => {
	di(useAIMateExperienceTracker);
	const successTriggerRef = useRef(trigger);

	const experienceTracker = useAIMateExperienceTracker();

	useEffect(() => {
		if (successTriggerRef.current === 'mount') {
			experienceTracker.succeed({
				name,
			});
		}
		if (successTriggerRef.current === 'unmount') {
			return () => {
				experienceTracker.succeed({
					name,
				});
			};
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return null;
};

export const ExperienceFailure = ({ error, name }: ExperienceProps & { error: Error }) => {
	di(useAIMateExperienceTracker);

	const experienceTracker = useAIMateExperienceTracker();

	useEffect(() => {
		experienceTracker.fail({
			name,
			error,
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return null;
};

export const ExperienceStart = ({ name }: ExperienceProps) => {
	di(useAIMateExperienceTracker);

	const experienceTracker = useAIMateExperienceTracker();

	useMemo(() => {
		experienceTracker.start({
			id: name,
			name,
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return null;
};
